import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getStationeryPurchaseOrderById } from 'src/redux/slices/stationery-purchase-order';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import StationeryPurchaseOrderNewEditForm from '../stationery-purchase-order-new-edit-form';

type Props = {
  id: string;
};

export default function StationeryPurchaseOrderEditView({ id }: Props) {
  const { order, status, error } = useAppSelector((state) => state.stationeryPurchaseOrder);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getStationeryPurchaseOrderById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load purchase order details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.stationeryPurchaseOrder.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Purchase Orders
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && order) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <CustomBreadcrumbs
          heading="Edit Purchase Order"
          links={[
            {
              name: 'Purchase Orders',
              href: paths.dashboard.stationeryPurchaseOrder.root,
            },
            { name: 'Edit Purchase Order' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <StationeryPurchaseOrderNewEditForm currentPurchaseOrder={order} />
      </Container>
    );
  }

  return <span />;
}
